import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { JUDGE_POSITIONS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { scribeSeats } from "../local/db";

// #524 — resolve the signed-in scribe's assignments across the (active org's)
// shows: the distinct { showId, show, ring, position } tuples where a class's
// scribeAssignments[position] includes the scribe's Cognito sub (#396). Used to
// auto-skip the show/ring picker when there is exactly one assignment.
//
// #2361 — this runs at login (autoSkipEligible in InitialSetup: no session yet,
// or the picker freshly unlocked), and the `classes` fan-out below already
// spans EVERY show the org has ever run, not just the one the scribe is about
// to pick. That makes it the earliest point a scribe's own assigned classes
// are ever seen client-side, org-wide — so this is where the scribe's own
// classId+position gets written through to the durable `scribeSeats` store
// (see local/db.js), rather than waiting for the scribe to individually open
// ClassList or a ride for each class. Without this, a class assigned after
// the scribe's last ClassList visit for that specific show (or one outside
// today's date window) stays unresolvable offline until they happen to view
// it online.
//
// This does NOT write into the shared `classes` cache: this endpoint
// (POST /class/batch, api.classes.listClassesForShows) returns RAW class
// rows, missing fields listClasses adds (scheduledTime, fvdPushState — see
// class.js's listClassesForShows handler comment), so writing them into the
// store useAssignedPosition/useClassesForShow read from would downgrade an
// already-enriched cached row. `scribeSeats` only ever needs classId +
// position, so it sidesteps that entirely — see local/db.js.
//
// Returns { assignments, loading }. `assignments` is null until resolved.
export function useScribeAssignments(shows) {
  const api = useScoreApi();
  const { user } = useAuth();
  const sub = user?.profile?.sub ?? null;
  const [assignments, setAssignments] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait until the shows list is available.
    if (!shows) {
      setAssignments(null);
      setLoading(true);
      return undefined;
    }
    if (!sub) {
      setAssignments([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // #1961 — one batched call across every show instead of one
        // listClasses HTTP round trip per show: `shows` here is
        // useShows()'s full org-scoped list (every show the org has ever
        // run, unfiltered by date, see handlers/show.js), not just the
        // scribe's current assignments, so this fan-out grows with the
        // org's whole show history rather than staying small.
        const showsById = new Map(shows.map((show) => [show.id, show]));
        const classes = shows.length
          ? unwrap(
              await api.classes.listClassesForShows({
                showIds: shows.map((show) => show.id),
              })
            )
          : [];
        const tuples = [];
        const seatRows = [];
        const staleClassIds = [];
        for (const c of classes) {
          const show = showsById.get(c.showId);
          if (!show) continue;
          const positions = [];
          for (const position of JUDGE_POSITIONS) {
            const ids = c.scribeAssignments?.[position] ?? [];
            if (ids.includes(sub)) {
              positions.push(position);
              tuples.push({
                showId: show.id,
                show,
                ring: c.ring ?? null,
                position,
              });
            }
          }
          if (positions.length) {
            seatRows.push({ classId: c.id, positions });
          } else {
            // #2361 — listClassesForShows is UNFILTERED (every class across
            // these shows, not just this scribe's own), so a class reaching
            // here with zero matching positions is positive proof the sub is
            // NOT named on it — the only place that proof exists, since
            // useClassesForShow's own fetch is `mine=true`-scoped and a class
            // missing from THAT list proves nothing. Collect it for pruning
            // below so a revoked assignment's durable seat row doesn't
            // outlive the revocation.
            staleClassIds.push(c.id);
          }
        }
        if (cancelled) return;
        // #2361 — see the file-header comment: persist the scribe's own
        // classId+position(s) to the durable scribeSeats store now, not just
        // the derived show/ring/position tuples below. Fire-and-forget — a
        // rejected write must not fail assignment resolution itself.
        if (seatRows.length) {
          scribeSeats.putAllForScribe(seatRows, sub).catch(() => {});
        }
        // #2361 — prune any stale seat row a REVOKED assignment left behind
        // (see staleClassIds above). Fire-and-forget, same reasoning as the
        // write above.
        if (staleClassIds.length) {
          scribeSeats.pruneForScribe(staleClassIds, sub).catch(() => {});
        }
        // Dedupe by show + ring + position — many classes share an assignment.
        const seen = new Set();
        const distinct = [];
        for (const t of tuples) {
          const key = `${t.showId}|${t.ring}|${t.position}`;
          if (!seen.has(key)) {
            seen.add(key);
            distinct.push(t);
          }
        }
        setAssignments(distinct);
      } catch {
        if (!cancelled) setAssignments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, shows, sub]);

  return { assignments, loading };
}

// #627 — the session is just the show, so the picker can be skipped whenever all
// of a scribe's assignments live in ONE show (however many classes/positions
// those are — position is resolved per class). Returns that show, or null.
export function soleShow(assignments) {
  if (!assignments || assignments.length === 0) return null;
  const showIds = new Set(assignments.map((a) => a.showId));
  return showIds.size === 1 ? assignments[0].show : null;
}
